import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import apiClient from '../../utils/axios';
import { setAuthorizationToken } from '../../utils/axios';
import { useFormValidation } from '../../utils/validation';
import GoogleSignInButton from './GoogleSignInButton';
import './auth.css';

const SignUpPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [validatedInvitation, setValidatedInvitation] = useState(null);
  const ssoEnabled = searchParams.get('sso') === 'true'
    || location.state?.validatedInvitation?.sso === true;
  const [ssoConfig, setSsoConfig] = useState(null);
  const [formData, setFormData] = useState({
    invitation_id: '',
    tenant_id: '',
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const {
    fieldErrors,
    fieldValid,
    touched,
    handleBlur: handleValidationBlur,
    handleFieldChange,
    validateAllFields,
    getFieldClass
  } = useFormValidation(['email', 'password', 'confirmPassword', 'first_name', 'last_name']);

  // Handle field blur (when user leaves field)
  const handleBlur = (e) => {
    const { name } = e.target;
    handleValidationBlur(name, formData[name], formData);
  };

  // Handle field change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    const newFormData = {
      ...formData,
      [name]: value
    };
    
    setFormData(newFormData);

    // If field was touched, validate in real-time
    handleFieldChange(name, value, newFormData);

    // Also validate confirmPassword if password changes
    if (name === 'password' && touched.confirmPassword) {
      handleFieldChange('confirmPassword', formData.confirmPassword, newFormData);
    }
  };

  useEffect(() => {
    // Check for validated invitation from navigation state (new flow)
    const navigationState = location.state?.validatedInvitation;
    if (navigationState) {
      setValidatedInvitation(navigationState);
      setFormData(prev => ({
        ...prev,
        invitation_id: navigationState.invitation_id,
        tenant_id: navigationState.tenant_id,
        email: navigationState.email
      }));
    } else {
      // Legacy flow - check URL parameters (redirect to validation)
      const invitationId = searchParams.get('invitation');
      if (invitationId) {
        const ssoParam = searchParams.get('sso') === 'true' ? '&sso=true' : '';
        navigate(`/invitation?invitation=${invitationId}${ssoParam}`);
      } else {
        setError('No invitation provided. Please use a valid invitation link.');
      }
    }
  }, [location.state, searchParams, navigate]);

  useEffect(() => {
    if (!ssoEnabled) return;
    apiClient.get('/iam/sso/config')
      .then(res => setSsoConfig(res.data))
      .catch(() => setSsoConfig(null));
  }, [ssoEnabled]);

  const handleSSOSuccess = async (idToken) => {
    if (!formData.invitation_id || !formData.tenant_id) {
      setError('Invalid invitation data');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.post('/iam/sso/signup', {
        provider: 'google',
        id_token: idToken,
        invitation_id: formData.invitation_id,
        tenant_id: formData.tenant_id,
      });
      const data = response.data;
      if (data.success) {
        if (data.authorization_token) {
          setAuthorizationToken(data.authorization_token);
        } else if (data.token) {
          setAuthorizationToken(data.token);
        }
        localStorage.setItem('user', JSON.stringify(data.user));
        navigate('/dashboard');
      } else {
        setError(data.message || 'SSO sign-up failed');
      }
    } catch (err) {
      if (err.response && err.response.data) {
        setError(err.response.data.message || 'SSO sign-up failed');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate form before submission
    if (!validateAllFields(formData)) {
      setLoading(false);
      setError('Please fix the errors above before submitting');
      return;
    }

    try {
      const response = await apiClient.post('/iam/signup', {
        invitation_id: formData.invitation_id,
        tenant_id: formData.tenant_id,
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name
      });

      const data = response.data;

      if (data.success) {
        // Identity cookie set by server; set in-memory authz token if present
        if (data.authorization_token) {
          setAuthorizationToken(data.authorization_token);
        } else if (data.token) {
          setAuthorizationToken(data.token);
        }
        localStorage.setItem('user', JSON.stringify(data.user));
        navigate('/dashboard');
      } else {
        setError(data.message || 'Sign-up failed');
      }
    } catch (err) {
      if (err.response && err.response.data) {
        setError(err.response.data.message || 'Sign-up failed');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Join TouchFlow</h1>
          {validatedInvitation && (
            <div className="invitation-details">
              <p>You've been invited to join <strong>{validatedInvitation.tenant_name}</strong></p>
              <p>Workspace: <strong>{validatedInvitation.tenant_code}</strong></p>
              <p>Email: <strong>{validatedInvitation.email}</strong></p>
              <p>Role: <strong>{validatedInvitation.role}</strong></p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className={`form-group ${getFieldClass('email')}`}>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter your email"
              disabled={validatedInvitation}
              required
            />
            <div className="validation-message">
              {fieldErrors.email && (
                <span className="field-error">{fieldErrors.email}</span>
              )}
              {fieldValid.email && !fieldErrors.email && (
                <span className="field-success">✓ Valid email address</span>
              )}
              {validatedInvitation && !fieldErrors.email && !fieldValid.email && (
                <small className="form-hint">This field is locked based on your invitation</small>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className={`form-group ${getFieldClass('first_name')}`}>
              <label htmlFor="first_name">First Name</label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter your first name"
                required
              />
              <div className="validation-message">
                {fieldErrors.first_name && (
                  <span className="field-error">{fieldErrors.first_name}</span>
                )}
                {fieldValid.first_name && !fieldErrors.first_name && (
                  <span className="field-success">✓ Valid name</span>
                )}
              </div>
            </div>

            <div className={`form-group ${getFieldClass('last_name')}`}>
              <label htmlFor="last_name">Last Name</label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter your last name"
                required
              />
              <div className="validation-message">
                {fieldErrors.last_name && (
                  <span className="field-error">{fieldErrors.last_name}</span>
                )}
                {fieldValid.last_name && !fieldErrors.last_name && (
                  <span className="field-success">✓ Valid name</span>
                )}
              </div>
            </div>
          </div>

          <div className={`form-group ${getFieldClass('password')}`}>
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Create a password (min 8 characters)"
              required
              minLength={8}
            />
            <div className="validation-message">
              {fieldErrors.password && (
                <span className="field-error">{fieldErrors.password}</span>
              )}
              {fieldValid.password && !fieldErrors.password && (
                <span className="field-success">✓ Strong password</span>
              )}
              {!touched.password && !fieldErrors.password && !fieldValid.password && (
                <small className="form-hint">Must include uppercase, lowercase, number, and special character</small>
              )}
            </div>
          </div>

          <div className={`form-group ${getFieldClass('confirmPassword')}`}>
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Confirm your password"
              required
            />
            <div className="validation-message">
              {fieldErrors.confirmPassword && (
                <span className="field-error">{fieldErrors.confirmPassword}</span>
              )}
              {fieldValid.confirmPassword && !fieldErrors.confirmPassword && (
                <span className="field-success">✓ Passwords match</span>
              )}
            </div>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        {ssoEnabled && ssoConfig?.google_client_id && validatedInvitation && (
          <>
            <div className="sso-divider"><span>or sign up with</span></div>
            <GoogleSignInButton
              clientId={ssoConfig.google_client_id}
              onSuccess={handleSSOSuccess}
              onError={(msg) => setError(msg)}
              disabled={loading}
            />
          </>
        )}

        <div className="auth-footer">
          <p>Already have an account? <a href="/login">Sign in here</a></p>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage; 